import { cn } from './cn';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md';

const BASE =
  'relative inline-flex shrink-0 select-none items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors duration-150 [&_svg]:shrink-0';

const VARIANTS: Record<ButtonVariant, { base: string; hover: string }> = {
  primary: {
    base: 'bg-accent text-accent-fg shadow-sm',
    hover: 'hover:bg-accent-hover active:bg-accent-hover',
  },
  secondary: {
    base: 'border border-border-strong bg-surface text-fg shadow-sm aria-pressed:border-accent/40 aria-pressed:bg-accent-muted aria-pressed:text-accent',
    hover: 'hover:bg-surface-muted active:bg-surface-muted',
  },
  ghost: {
    base: 'text-fg-muted aria-pressed:bg-accent-muted aria-pressed:text-accent',
    hover: 'hover:bg-surface-muted hover:text-fg active:bg-surface-muted',
  },
  danger: {
    base: 'bg-danger text-danger-contrast shadow-sm',
    hover: 'hover:bg-danger-hover active:bg-danger-hover',
  },
};

const SIZES: Record<ButtonSize, string> = {
  sm: 'h-8 gap-1.5 px-3 [&_svg]:size-3.5',
  md: 'h-9 gap-2 px-4 [&_svg]:size-4',
};

const ICON_SIZES: Record<ButtonSize, string> = {
  sm: 'size-8 [&_svg]:size-4',
  md: 'size-9 [&_svg]:size-4',
};

export interface ButtonStyleOptions {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Square icon-only geometry. */
  iconOnly?: boolean;
  /** Dims the control and drops hover feedback. */
  disabled?: boolean;
  /** Pending action: drops hover feedback without dimming. */
  loading?: boolean;
  className?: string;
}

/**
 * Button classes for elements that must look like a button but cannot be a
 * `Button`, such as a `<label>` for a file input or a download `<a>`.
 */
export const buttonStyles = ({
  variant = 'secondary',
  size = 'md',
  iconOnly = false,
  disabled = false,
  loading = false,
  className,
}: ButtonStyleOptions = {}): string =>
  cn(
    BASE,
    VARIANTS[variant].base,
    iconOnly ? ICON_SIZES[size] : SIZES[size],
    disabled
      ? 'cursor-not-allowed opacity-50'
      : loading
        ? 'cursor-progress'
        : VARIANTS[variant].hover,
    className
  );
